// src/pages/auth/pages/LoginPage.jsx
import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ReactComponent as UserIcon } from "../../../../assets/icons/fi-user.svg";
import { ReactComponent as LockIcon } from "../../../../assets/icons/lock.svg";
import InputField from "../../components/InputFeild";
import { userService } from "../../../../services/api/authenService";
import { AuthContext } from "../../../../contexts/AuthContext";
import { toast } from "react-toastify";

const LoginPage = () => {
  const { dispatch } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    identifier: "",
    password: "",
    rememberMe: false,
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [focusField, setFocusField] = useState(null);
  const [shakeKey, setShakeKey] = useState(0); // trigger “shake” khi lỗi

  const idRef = useRef(null);

  // Load "remember me"
  useEffect(() => {
    const savedIdentifier = localStorage.getItem("savedIdentifier");
    const rememberMe = localStorage.getItem("rememberMe") === "true";

    if (rememberMe && savedIdentifier) {
      setFormData({
        identifier: savedIdentifier,
        password: "",
        rememberMe,
      });
    }
  }, []);

  useEffect(() => {
    // Auto focus ô đầu tiên
    idRef.current?.focus?.();
  }, []);

  const iconColor = (field) => (focusField === field ? "#1D4ED8" : "#9E9E9E");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((s) => ({
      ...s,
      [name]:
        type === "checkbox"
          ? checked
          : name === "identifier"
            ? value.trim()
            : value,
    }));
    setErrors((s) => ({ ...s, [name]: "" }));
  };

  // Validate đơn giản: có thể nâng lên theo policy
  const validateForm = () => {
    const newErrors = {};
    if (!formData.identifier) {
      newErrors.identifier = "Vui lòng nhập tài khoản/MST/SĐT.";
    }
    if ((formData.password || "").length < 6) {
      newErrors.password = "Mật khẩu phải có ít nhất 6 ký tự.";
    }
    return newErrors;
  };

  const canSubmit = useMemo(
    () => !!formData.identifier && !!formData.password && !isLoading,
    [formData.identifier, formData.password, isLoading],
  );

  // CHỈ lưu identifier, KHÔNG lưu password
  const persistRememberMe = (remember) => {
    // Giữ hành vi cũ
    localStorage.setItem("rememberMe", remember ? "true" : "false");
    if (remember) {
      // Chỉ lưu tài khoản
      localStorage.setItem("savedIdentifier", formData.identifier);
    } else {
      // Xóa tất cả khi không tick "Nhớ mật khẩu"
      localStorage.removeItem("savedIdentifier");
    }
    // Đảm bảo xóa password cũ nếu có (từ version cũ)
    localStorage.removeItem("savedPassword");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setShakeKey((k) => k + 1);
      return;
    }

    setIsLoading(true);
    try {
      const response = await userService.log_in(
        formData.identifier,
        formData.password,
      );

      const role = response?.account?.role;
      if (role === "Admin" || role === "SuperAdmin") {
        toast.dismiss();
        toast.error(
          "Bạn không có quyền truy cập trang này. Vui lòng đăng ký mới sử dụng số điện thoại!",
        );
        setShakeKey((k) => k + 1);
        return;
      }

      if (response && response.token) {
        // Lưu cả token và refreshToken
        dispatch({
          type: "LOGIN",
          payload: {
            token: response.token,
            refreshToken: response.refreshToken,
            user: response.account,
          },
        });

        persistRememberMe(formData.rememberMe);
        toast.success("Đăng nhập thành công!");
        navigate("/");
      } else {
        setErrors({
          identifier: "Tài khoản đăng nhập không đúng.",
          password: "Mật khẩu không đúng.",
        });
        setShakeKey((k) => k + 1);
        toast.dismiss();
        toast.error("Tài khoản/mật khẩu không đúng. Vui lòng kiểm tra lại.");
      }
    } catch (error) {
      console.error("Login error:", error);
      setShakeKey((k) => k + 1);
      const msg =
        error?.response?.data?.message ||
        "Đăng nhập thất bại, vui lòng kiểm tra lại.";
      toast.dismiss();
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFocus = (field) => setFocusField(field);
  const handleBlur = () => setFocusField(null);

  return (
    <div className="w-full h-full flex flex-col items-center justify-center">
      {/* Header mobile */}
      <div className="flex-col items-center justify-center mb-3 space-y-1 desktop:hidden flex">
        <div className="flex items-center justify-center">
          <img
            alt="Web logo"
            className="size-[46px] 3xl:size-[57px]"
            src="/logos/logo_vector.svg"
          />
          <h1 className="font-be-vietnam-semiBold font-bold text-[34px]  block">
            Sohuutritue.vn
          </h1>
        </div>
        <p className=" text-center max-w-xl">Nền tảng số Sở hữu trí tuệ</p>
      </div>

      {/* Header desktop */}
      <div className="flex items-center mb-3 desktop:mb-6 animate-fade-in">
        <img
          src="/logos/logo_vector.svg"
          alt="Web logo"
          className="size-[36px] 3xl:size-[57px] desktop:block hidden 3xl:hidden"
        />
        <h1 className="text-2xl font-be-vietnam-semiBold font-bold ml-2">
          Đăng nhập
        </h1>
      </div>

      {/* Form (1 form responsive, tránh trùng lặp JSX) */}
      <form
        key={shakeKey}
        onSubmit={handleSubmit}
        className="w-full  flex flex-col gap-4 animate-slide-in"
      >
        <InputField
          label="Tên tài khoản (MST/SĐT)"
          type="text"
          name="identifier"
          placeholder="Nhập số điện thoại / mã số thuế"
          IconComponent={UserIcon}
          value={formData.identifier}
          onChange={handleChange}
          error={errors.identifier}
          iconColor={iconColor("identifier")}
          onFocus={() => handleFocus("identifier")}
          onBlur={handleBlur}
          inputRef={idRef}
          autoComplete="username"
        />

        <InputField
          label="Mật khẩu"
          type={showPassword ? "text" : "password"}
          name="password"
          placeholder="Mật khẩu"
          IconComponent={LockIcon}
          value={formData.password}
          onChange={handleChange}
          toggleVisibility={() => setShowPassword((prev) => !prev)}
          showPassword={showPassword}
          error={errors.password}
          iconColor={iconColor("password")}
          onFocus={() => handleFocus("password")}
          onBlur={handleBlur}
          autoComplete="current-password"
        />

        <div className="flex items-center justify-between">
          <label className="flex items-center text-sm text-gray-600">
            <input
              type="checkbox"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleChange}
              className="mr-2 accent-blue-500 font-sf-medium"
            />
            Nhớ tài khoản
          </label>
          <Link
            to="/forgot-password"
            className="text-sm font-sf-medium text-blue-500 hover:underline"
          >
            Quên mật khẩu
          </Link>
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          className={`group w-full mt-1 font-sf-medium text-white py-3 rounded-lg transition-all ${canSubmit
              ? "bg-blue-500 hover:bg-blue-600 active:scale-[0.99] shadow-lg shadow-blue-500/25"
              : "bg-blue-300/70 cursor-default"
            }`}
        >
          <span className="inline-flex items-center justify-center gap-2">
            {isLoading && <span className="spinner" aria-hidden />}
            {isLoading ? "Đang đăng nhập..." : "Đăng nhập"}
          </span>
        </button>
      </form>

      {/* Signup Link */}
      <div
        className="mt-4 text-sm opacity-0 animate-fade-in"
        style={{ animationDelay: "120ms", animationFillMode: "forwards" }}
      >
        Chưa có tài khoản?{" "}
        <Link
          to="/signup"
          className="text-blue-500 font-sf-medium hover:underline"
        >
          Đăng ký
        </Link>
      </div>

      {/* Local CSS animations & focus styles */}
      <style>{`
        .spinner {
          width: 16px; height: 16px; border-radius: 9999px;
          border: 2px solid rgba(255,255,255,0.6);
          border-top-color: white;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .animate-fade-in-up {
          animation: fadeInUp 380ms cubic-bezier(0.22,1,0.36,1) forwards;
        }
        .animate-fade-in {
          animation: fadeIn 420ms ease forwards;
        }
        .animate-slide-in {
          animation: slideIn 420ms cubic-bezier(0.22,1,0.36,1) forwards;
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

        /* Tô điểm focus chung cho input trong InputField (nếu component dùng className pass-through) */
        input:focus, input:focus-visible {
          outline: none;
          box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.12);
          transition: box-shadow 200ms ease, transform 200ms ease;
          transform: translateZ(0) scale(1.01);
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
